import { Alert, Button, Modal, toast } from "@cafe/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/store";
import { http } from "@/shared/api/client";
import { useCan } from "@/shared/hooks/session";
import { formatDateTime } from "@/shared/lib/options";
import { useRealtime } from "@/shared/realtime/socket";
import { handleMutationError } from "@/shared/ui/query-error";

type AccessRequest = { id: string; mode: "read_only" | "full"; reason: string; status: string; requestedAt: string; expiresAt: string; platformUserName: string | null };
const key = ["support-access"] as const;

/**
 * Запрос сотрудника Coca на вход в кабинет: без разрешения владельца или управляющего (`support.grant_access`)
 * поддержка не войдёт ни в режиме просмотра, ни с изменениями. Окно появляется само — по уведомлению и при входе.
 */
export function SupportAccessPrompt() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const can = useCan();
  const impersonating = useAuth((s) => !!s.impersonation);
  const enabled = can("support.grant_access") && !impersonating;
  const q = useQuery({
    queryKey: key, enabled, refetchInterval: 60_000,
    queryFn: ({ signal }) => http.get<{ pending: AccessRequest[] }>("/support-access", undefined, signal),
  });
  useRealtime<{ type: string }>("notification.created", (e) => e.data.type === "support.access" && void qc.invalidateQueries({ queryKey: key }));
  const [later, setLater] = useState<string[]>([]);
  const [busy, setBusy] = useState<"approve" | "deny" | null>(null);
  const r = q.data?.pending.find((x) => !later.includes(x.id) && new Date(x.expiresAt) > new Date());
  if (!enabled || !r) return null;

  const decide = async (action: "approve" | "deny") => {
    setBusy(action);
    try {
      await http.post(`/support-access/${r.id}/${action}`);
      toast.success(t(action === "approve" ? "supportAccess.approved" : "supportAccess.denied"));
    } catch (e) {
      await handleMutationError(e);
    } finally {
      setBusy(null);
      void qc.invalidateQueries({ queryKey: key });
    }
  };

  return (
    <Modal open onOpenChange={(o) => !o && setLater([...later, r.id])} title={t("supportAccess.title")}
      description={t("supportAccess.description", { name: r.platformUserName ?? "Coca" })}
      footer={<>
        <Button loading={busy === "approve"} disabled={!!busy} onClick={() => void decide("approve")}>{t("supportAccess.approve")}</Button>
        <Button variant="outline" loading={busy === "deny"} disabled={!!busy} onClick={() => void decide("deny")}>{t("supportAccess.deny")}</Button>
      </>}>
      <div className="flex flex-col gap-3 text-sm">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
          <dt className="text-muted">{t("supportAccess.mode")}</dt>
          <dd className="font-medium">{t(r.mode === "full" ? "supportAccess.modeFull" : "supportAccess.modeReadOnly")}</dd>
          <dt className="text-muted">{t("supportAccess.reason")}</dt>
          <dd className="break-words">{r.reason}</dd>
          <dt className="text-muted">{t("supportAccess.until")}</dt>
          <dd>{formatDateTime(r.expiresAt)}</dd>
        </dl>
        <Alert tone={r.mode === "full" ? "warning" : "info"}>{t(r.mode === "full" ? "supportAccess.fullHint" : "supportAccess.readOnlyHint")}</Alert>
      </div>
    </Modal>
  );
}
